import React from "react";
import { Typography, Tooltip } from "@mui/material";

// Helper function to get appropriate emoji for channel
const getChannelIcon = (channel) => {
  const channelLower = channel?.toLowerCase() || '';
  
  if (channelLower.includes('organic')) return '🌱';
  if (channelLower.includes('direct')) return '📬';
  if (channelLower.includes('social')) return '👥';
  if (channelLower.includes('referral')) return '🔗';
  if (channelLower.includes('email')) return '✉️';
  if (channelLower.includes('paid')) return '💰';
  if (channelLower.includes('display')) return '🖼️';
  if (channelLower.includes('cpc') || channelLower.includes('ppc')) return '💸';
  
  // Default icon if no specific match
  return '📊';
};

const BySource = ({ analyticsData, themeBg }) => {
  const osData = analyticsData?.sessions?.byOS || []; // Show all OS data
  const allSourceData = analyticsData?.sessions?.bySource || [];
  const allChannelData = analyticsData?.sessions?.byChannel || [];

  // Limit to showing only top 5 items for sources and channels
  const sourceData = allSourceData.slice(0, 5);
  const channelData = allChannelData.slice(0, 5);

  // Calculate remaining items (only for sources and channels)
  const remainingSource = Math.max(0, allSourceData.length - 5);
  const remainingChannel = Math.max(0, allChannelData.length - 5);

  return (
    <div
      style={{
        backgroundColor: themeBg || "#6D28D9",
        borderRadius: "12px",
        padding: "20px",
        color: "#FFFFFF",
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
      }}
    >
      {/* Main Section Header */}
      <Typography
        variant="h6"
        style={{
          fontWeight: "bold",
          marginBottom: "15px",
          borderBottom: "2px solid rgba(255, 255, 255, 0.5)",
          paddingBottom: "8px",
        }}
      >
        Traffic Sources
      </Typography>

      {/* Operating Systems Section */}
      <div
        style={{
          backgroundColor: "rgba(0, 0, 0, 0.1)",
          borderRadius: "8px",
          padding: "12px",
          marginBottom: "20px",
        }}
      >
        <Typography
          variant="body1"
          style={{
            fontWeight: "bold",
            fontSize: "15px",
            marginBottom: "10px",
            borderBottom: "1px solid rgba(255, 255, 255, 0.2)",
            paddingBottom: "5px",
          }}
        >
          Operating Systems
        </Typography>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "8px",
            paddingBottom: "5px",
            borderBottom: "1px solid rgba(255, 255, 255, 0.3)",
          }}
        >
          <Typography
            variant="body2"
            style={{
              flex: 3,
              fontWeight: "bold",
              fontSize: "14px",
            }}
          >
            OS Name
          </Typography>
          <Typography
            variant="body2"
            style={{
              flex: 2,
              fontWeight: "bold",
              fontSize: "14px",
              textAlign: "center",
            }}
          >
            Sessions
          </Typography>
          <Typography
            variant="body2"
            style={{
              flex: 1,
              fontWeight: "bold",
              fontSize: "14px",
              textAlign: "right",
            }}
          >
            %
          </Typography>
        </div>

        {/* OS Data Rows - show all OS data without limitation */}
        {osData.length > 0 ? (
          osData.map((item, index) => (
            <div
              key={index}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "6px 4px",
                marginBottom: "4px",
                backgroundColor:
                  index % 2 === 0 ? "rgba(0, 0, 0, 0.1)" : "transparent",
                borderRadius: "4px",
              }}
            >
              <Typography
                variant="body2"
                style={{
                  flex: 3,
                  fontSize: "14px",
                  wordBreak: "break-word",
                }}
              >
                {item.os}
              </Typography>
              <Typography
                variant="body2"
                style={{
                  flex: 2,
                  fontSize: "14px",
                  textAlign: "center",
                }}
              >
                {item.sessions}
              </Typography>
              <Typography
                variant="body2"
                style={{
                  flex: 1,
                  fontSize: "14px",
                  textAlign: "right",
                }}
              >
                {item.percentage}
              </Typography>
            </div>
          ))
        ) : (
          <Typography variant="body2" style={{ textAlign: "center" }}>
            No OS data available
          </Typography>
        )}
      </div>

      {/* Traffic Source Section */}
      <div
        style={{
          backgroundColor: "rgba(255, 255, 255, 0.1)",
          borderRadius: "8px",
          padding: "12px",
          marginBottom: "20px",
        }}
      >
        <Typography
          variant="body1"
          style={{
            fontWeight: "bold",
            fontSize: "15px",
            marginBottom: "10px",
            borderBottom: "1px solid rgba(255, 255, 255, 0.2)",
            paddingBottom: "5px",
          }}
        >
          Traffic Sources
        </Typography>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "8px",
            paddingBottom: "5px",
            borderBottom: "1px solid rgba(255, 255, 255, 0.3)",
          }}
        >
          <Typography
            variant="body2"
            style={{
              flex: 3,
              fontWeight: "bold",
              fontSize: "14px",
            }}
          >
            Source
          </Typography>
          <Typography
            variant="body2"
            style={{
              flex: 2,
              fontWeight: "bold",
              fontSize: "14px",
              textAlign: "center",
            }}
          >
            Sessions
          </Typography>
        </div>

        {/* Source Data Rows with improved visualization */}
        {sourceData.length > 0 ? (
          sourceData.map((item, index) => (
            <div
              key={index}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "6px 4px",
                marginBottom: "4px",
                backgroundColor:
                  index % 2 === 0 ? "rgba(255, 255, 255, 0.07)" : "transparent",
                borderRadius: "4px",
              }}
            >
              <Typography
                variant="body2"
                style={{
                  flex: 3,
                  fontSize: "14px",
                  wordBreak: "break-word",
                }}
              >
                {item.source}
              </Typography>
              <Typography
                variant="body2"
                style={{
                  flex: 2,
                  fontSize: "14px",
                  textAlign: "center",
                }}
              >
                {item.sessions}
              </Typography>
            </div>
          ))
        ) : (
          <Typography variant="body2" style={{ textAlign: "center" }}>
            No source data available
          </Typography>
        )}

        {/* Show remaining count if applicable */}
        {remainingSource > 0 && (
          <div
            style={{
              textAlign: "center",
              fontSize: "12px",
              fontStyle: "italic",
              marginTop: "8px",
              opacity: 0.8,
            }}
          >
            + {remainingSource} more source{remainingSource !== 1 ? "s" : ""}...
          </div>
        )}
      </div>

      {/* Traffic Channel Section */}
      <div
        style={{
          backgroundColor: "rgba(0, 0, 0, 0.15)",
          borderRadius: "8px",
          padding: "12px",
        }}
      >
        <Typography
          variant="body1"
          style={{
            fontWeight: "bold",
            fontSize: "15px",
            marginBottom: "10px",
            borderBottom: "1px solid rgba(255, 255, 255, 0.2)",
            paddingBottom: "5px",
          }}
        >
          Traffic Channels
        </Typography>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "8px",
            paddingBottom: "5px",
            borderBottom: "1px solid rgba(255, 255, 255, 0.3)",
          }}
        >
          <Typography
            variant="body2"
            style={{
              flex: 3,
              fontWeight: "bold",
              fontSize: "14px",
            }}
          >
            Channel
          </Typography>
          <Typography
            variant="body2"
            style={{
              flex: 2,
              fontWeight: "bold",
              fontSize: "14px",
              textAlign: "center",
            }}
          >
            Sessions
          </Typography>
        </div>

        {/* Channel Data Rows with improved visualization */}
        {channelData.length > 0 ? (
          channelData.map((item, index) => (
            <div
              key={index}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "6px 4px",
                marginBottom: "4px",
                backgroundColor:
                  index % 2 === 0 ? "rgba(0, 0, 0, 0.1)" : "transparent",
                borderRadius: "4px",
              }}
            >
              <Typography
                variant="body2"
                style={{
                  flex: 3,
                  fontSize: "14px",
                  wordBreak: "break-word",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <Tooltip title={item.channel} placement="top">
                  <span style={{ fontSize: "16px" }}>
                    {getChannelIcon(item.channel)}
                  </span>
                </Tooltip>
                {item.channel}
              </Typography>
              <Typography
                variant="body2"
                style={{
                  flex: 2,
                  fontSize: "14px",
                  textAlign: "center",
                }}
              >
                {item.sessions}
              </Typography>
            </div>
          ))
        ) : (
          <Typography variant="body2" style={{ textAlign: "center" }}>
            No channel data available
          </Typography>
        )}
      </div>
    </div>
  );
};

export default BySource;
